import React, { useMemo, useState } from 'react';
import { useLoaderData } from "react-router-dom";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { IsNullOrEmpty } from "../../../services/CustomService";
import { getHomepageTools, searchHomepageTools, updateHomepageTool } from "../../../data/admin/homepageTools";

export async function loader() {
    const tools = await getHomepageTools();
    return { tools };
}

export default function AdminHomepageToolsPage() {
    const { tools } = useLoaderData();
    const [homepageTools, setHomepageTools] = useState(tools ?? []);
    const [searchType, setSearchType] = useState('latest');
    const [searchValue, setSearchValue] = useState('10');
    const [searchResults, setSearchResults] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [updatingPublicId, setUpdatingPublicId] = useState(null);

    const searchOptions = useMemo(() => ([
        { label: 'Dernières annonces', value: 'latest' },
        { label: 'PublicId', value: 'publicId' },
        { label: 'CustomUrl', value: 'customUrl' },
        { label: 'Email utilisateur', value: 'email' }
    ]), []);

    const sortHomepageTools = (items) => {
        return [...(items || [])].sort((a, b) => {
            const rankA = Number(a.rank ?? 0);
            const rankB = Number(b.rank ?? 0);
            if (rankA !== rankB) {
                return rankB - rankA;
            }
            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });
    };

    const normalizeTool = (tool, fallback = {}) => ({
        ...fallback,
        ...tool,
        publicId: tool?.publicId ?? tool?.PublicId ?? fallback.publicId,
        name: tool?.name ?? tool?.Name ?? fallback.name,
        brand: tool?.brand ?? tool?.Brand ?? fallback.brand,
        model: tool?.model ?? tool?.Model ?? fallback.model,
        base64Picture: tool?.base64Picture ?? tool?.Base64Picture ?? fallback.base64Picture,
        customUrl: tool?.customUrl ?? tool?.CustomUrl ?? fallback.customUrl,
        ownerName: tool?.ownerName ?? tool?.OwnerName ?? fallback.ownerName,
        ownerEmail: tool?.ownerEmail ?? tool?.OwnerEmail ?? fallback.ownerEmail,
        category: tool?.category ?? tool?.Category ?? fallback.category,
        priceDayCents: tool?.priceDayCents ?? tool?.PriceDayCents ?? fallback.priceDayCents,
        showOnHomepage: tool?.showOnHomepage ?? tool?.ShowOnHomepage ?? fallback.showOnHomepage,
        rank: tool?.rank ?? tool?.Rank ?? fallback.rank ?? 0
    });

    const handleSearch = async () => {
        if (!searchValue.trim()) {
            window.alert("Merci d'indiquer une valeur de recherche.");
            return;
        }

        setLoadingSearch(true);
        try {
            const results = await searchHomepageTools(searchType, searchValue.trim());
            setSearchResults((results ?? []).map((tool) => normalizeTool(tool)));
        } finally {
            setLoadingSearch(false);
        }
    };

    const updateLocalHomepageTool = (updatedTool) => {
        const normalized = normalizeTool(updatedTool);
        setHomepageTools((prev) => {
            const withoutTool = prev.filter((item) => item.publicId !== normalized.publicId);
            if (!normalized.showOnHomepage) {
                return sortHomepageTools(withoutTool);
            }
            return sortHomepageTools([...withoutTool, normalized]);
        });
        setSearchResults((prev) => prev.map((item) => item.publicId === normalized.publicId ? normalizeTool(normalized, item) : item));
    };

    const setHomepageState = async (tool, showOnHomepage, rank = tool.rank ?? 0) => {
        const publicId = tool.publicId;
        setUpdatingPublicId(publicId);
        try {
            const updated = await updateHomepageTool(publicId, {
                showOnHomepage,
                rank: Number(rank ?? 0)
            });
            updateLocalHomepageTool(normalizeTool(updated, tool));
        } finally {
            setUpdatingPublicId(null);
        }
    };

    const setRank = async (tool, rank) => {
        await setHomepageState(tool, true, rank);
    };

    const toolBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            {(!IsNullOrEmpty(rowData.base64Picture)) ? (
                <img className="smallPicture" alt={rowData.name} src={rowData.base64Picture || null} />
            ) : (
                <div className="no-picture no-picture-small"></div>
            )}
            <div>
                <div><strong>{rowData.name}</strong></div>
                <small className="text-muted">{[rowData.brand, rowData.model].filter(Boolean).join(' ')}</small>
            </div>
        </div>
    );

    const ownerBodyTemplate = (rowData) => (
        <div>
            <div>{rowData.ownerName}</div>
            <small className="text-muted">{rowData.ownerEmail}</small>
        </div>
    );

    const priceBodyTemplate = (rowData) => `${(rowData.priceDayCents ?? 0) / 100}€`;

    const rankBodyTemplate = (rowData) => {
        const currentTool = homepageTools.find((item) => item.publicId === rowData.publicId) ?? rowData;
        const currentRank = currentTool.rank ?? 0;

        return (
            <div className="flex align-items-center gap-2">
                <InputNumber
                    value={currentRank}
                    min={0}
                    max={9999}
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonIcon="pi pi-minus"
                    incrementButtonIcon="pi pi-plus"
                    inputStyle={{ width: '5rem' }}
                    disabled={updatingPublicId === rowData.publicId}
                    onValueChange={(event) => {
                        const rank = event.value ?? 0;
                        setHomepageTools((prev) => sortHomepageTools(prev.map((item) => item.publicId === rowData.publicId ? { ...item, rank } : item)));
                    }}
                />
                <Button
                    icon="pi pi-save"
                    text
                    rounded
                    aria-label="Enregistrer le rang"
                    loading={updatingPublicId === rowData.publicId}
                    onClick={() => setRank(currentTool, currentRank)}
                />
            </div>
        );
    };

    const currentActionsBodyTemplate = (rowData) => (
        <Button
            label="Retirer"
            icon="pi pi-times"
            size="small"
            severity="danger"
            loading={updatingPublicId === rowData.publicId}
            onClick={() => setHomepageState(rowData, false, rowData.rank ?? 0)}
        />
    );

    const searchActionsBodyTemplate = (rowData) => {
        const isAlreadySelected = homepageTools.some((tool) => tool.publicId === rowData.publicId);
        return (
            <Button
                label={isAlreadySelected ? "Déjà affiché" : "Ajouter"}
                icon={isAlreadySelected ? "pi pi-check" : "pi pi-plus"}
                size="small"
                severity={isAlreadySelected ? "secondary" : "success"}
                disabled={isAlreadySelected}
                loading={updatingPublicId === rowData.publicId}
                onClick={() => setHomepageState(rowData, true, rowData.rank ?? 0)}
            />
        );
    };

    return (
        <div>
            <h1>Matériel de saison</h1>
            <div className="card flex flex-column gap-3">
                <div>
                    <h2>Rubrique affichée sur la homepage</h2>
                    <p className="text-muted">La homepage affiche les 4 premiers outils actifs, triés par rang décroissant.</p>
                </div>
                <DataTable value={homepageTools} paginator rows={10} dataKey="publicId" showGridlines emptyMessage="Aucun matériel de saison configuré">
                    <Column header="Annonce" body={toolBodyTemplate} />
                    <Column field="category" header="Catégorie" />
                    <Column header="Prix/j" body={priceBodyTemplate} />
                    <Column header="Propriétaire" body={ownerBodyTemplate} />
                    <Column header="Rang" body={rankBodyTemplate} />
                    <Column header="" body={currentActionsBodyTemplate} />
                </DataTable>
            </div>

            <div className="card flex flex-column gap-3 mt-4">
                <div>
                    <h2>Ajouter une annonce</h2>
                    <div className="flex flex-column md:flex-row gap-2 align-items-start md:align-items-center">
                        <Dropdown
                            value={searchType}
                            options={searchOptions}
                            onChange={(event) => setSearchType(event.value)}
                            placeholder="Type de recherche"
                        />
                        <InputText
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            placeholder="Recherche"
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                        />
                        <Button label="Rechercher" icon="pi pi-search" size="small" loading={loadingSearch} onClick={handleSearch} />
                    </div>
                </div>
                <DataTable value={searchResults} paginator rows={10} dataKey="publicId" showGridlines emptyMessage="Aucun résultat">
                    <Column header="Annonce" body={toolBodyTemplate} />
                    <Column field="category" header="Catégorie" />
                    <Column header="Prix/j" body={priceBodyTemplate} />
                    <Column header="Propriétaire" body={ownerBodyTemplate} />
                    <Column header="" body={searchActionsBodyTemplate} />
                </DataTable>
            </div>
        </div>
    );
}
